import { Rule } from 'antd/es/form';
import { OrderByType } from 'config/constants/search.constants';
import { AuthTranscription, GeneralTranscription, UserTranscription } from 'config/constants/transcription.constant';
import UserService from 'config/services/user.service';
import { Address, User } from 'store/models/user.model';
import { store } from '../../../store';
import { DEFAULT_COUNTRY_DISPLAY } from 'store/models/setting.model';

const PASSWORD_MAX_LENGTH = 4;
const FAVORITES_MAX_ITEMS = 2;

export const getDniCopy = () => {
  const countryDisplay = store.getState().setting.countryDisplay || DEFAULT_COUNTRY_DISPLAY;
  return {
    label: countryDisplay.dniLabel,
    required: `Ingrese un ${countryDisplay.dniLabel.toLowerCase()}`,
    invalid: countryDisplay.dniInvalid,
    placeholder: countryDisplay.dniPlaceholder,
  };
};

const formatDni = (dni: string): string => dni.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

type IUserGenreSelectOption = {
  value: string;
  label: string;
};

const emailFieldRules: Rule[] = [
  { required: true, message: AuthTranscription.ERROR_EMPTY_MAIL_RULE },
  { type: 'email', message: AuthTranscription.ERROR_FORMAT_MAIL_RULE },
];

const passwordFielRules: Rule[] = [
  { required: true, message: AuthTranscription.ERROR_EMPTY_PASSWORD_RULE },
  { type: 'string', message: AuthTranscription.ERROR_TYPE_PASSWORD_RUlE },
  { min: 4, message: AuthTranscription.ERROR_PASSWORD_MIN },
  { max: 6, message: AuthTranscription.ERROR_PASSWORD_MAX },
];

const passwordLoginFiedRules: Rule[] = [{ required: true, message: AuthTranscription.ERROR_EMPTY_PASSWORD_RULE }];

const repeatPasswordFields: Rule[] = [
  ...passwordFielRules,
  ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value || getFieldValue('password') === value) return Promise.resolve();
      return Promise.reject(new Error(AuthTranscription.ERROR_REPEAT_PASSWORD_NOT_MATCH));
    },
  }),
];

const userNameFieldRules: Rule[] = [
  { required: true, message: UserTranscription.USER_NAME_RULE_REQUIRED },
  { type: 'string', message: UserTranscription.USER_NAME_RULE_TYPE },
  { min: 3, message: UserTranscription.USER_NAME_RULE_MIN },
  { max: 20, message: UserTranscription.USER_NAME_RULE_MAX },
];

const userLastNameFieldRules: Rule[] = [
  { required: true, message: UserTranscription.USER_LAST_NAME_RULE_REQUIRED },
  { type: 'string', message: UserTranscription.USER_LAST_NAME_RULE_TYPE },
  { min: 3, message: UserTranscription.USER_LAST_NAME_RULE_MIN },
  { max: 30, message: UserTranscription.USER_LAST_NAME_RULE_MAX },
];

const getUserDniFieldRules = (): Rule[] => [
  { required: true, message: getDniCopy().required },
  {
    validateTrigger: 'onBlur',
    validator: async (_, value) => {
      if (!value) return Promise.resolve();
      try {
        await UserService.validateDni(value);
        return Promise.resolve();
      } catch {
        return Promise.reject(new Error(getDniCopy().invalid));
      }
    },
  },
];

const DNI_SKIP_LIVE_VALIDATION = ['dni'];

const userEmailFieldRules: Rule[] = [
  { required: true, message: UserTranscription.USER_EMAIL_REQUIRED },
  { type: 'string', message: UserTranscription.USER_EMAIL_RULE_TYPE },
  { min: 10, message: UserTranscription.USER_EMAIL_RULE_MIN },
  { max: 50, message: UserTranscription.USER_EMAIL_RULE_MAX },
];

const userPhoneFieldRules: Rule[] = [
  { required: true, message: UserTranscription.USER_PHONE_REQUIRED },
  { type: 'string', message: UserTranscription.USER_PHONE_RULE_TYPE },
];

const userGenreFieldRules: Rule[] = [{ required: true, message: UserTranscription.USER_GENRE_RULE_REQUIRED }];

const userBirhDateRules: Rule[] = [{ type: 'object', message: UserTranscription.USER_BIRTH_DATE_RULE_TYPE }];

const translateUserGenre = (genre: string | undefined): string => {
  switch (genre) {
    case undefined:
      return '';
    case 'M':
      return UserTranscription.USER_GENRE_MALE;
    case 'F':
      return UserTranscription.USER_GENRE_FEMALE;
    default:
      return GeneralTranscription.OTHER;
  }
};

const translateUserStatus = (status: string | undefined): string => {
  switch (status) {
    case 'ACTIVE':
      return GeneralTranscription.ACTIVE;
    case 'INACTIVE':
      return GeneralTranscription.INACTIVE;
    default:
      return '';
  }
};

const handleGenreOption = (): Array<IUserGenreSelectOption> => {
  return [
    { value: 'M', label: 'Masculino' },
    { value: 'F', label: 'Femenino' },
    { value: 'O', label: 'Otro' },
  ];
};

const getSelectedUserAddress = (userProfile: User): Address | undefined => userProfile.address.find((item) => item.selected) ?? undefined;

const getUserPurchasesFootPrint = (page: number, orderBy: OrderByType): string => JSON.stringify({ page, orderBy });

const getUserFavoritesFootPrint = (favorites: string[]): string => JSON.stringify(favorites || []);

const UserUtils = {
  PASSWORD_MAX_LENGTH,
  FAVORITES_MAX_ITEMS,
  emailFieldRules,
  passwordFielRules,
  repeatPasswordFields,
  userNameFieldRules,
  userLastNameFieldRules,
  getUserDniFieldRules,
  DNI_SKIP_LIVE_VALIDATION,
  userEmailFieldRules,
  userPhoneFieldRules,
  userGenreFieldRules,
  userBirhDateRules,
  passwordLoginFiedRules,
  translateUserGenre,
  translateUserStatus,
  handleGenreOption,
  formatDni,
  getDniCopy,
  getSelectedUserAddress,
  getUserPurchasesFootPrint,
  getUserFavoritesFootPrint,
};
export default UserUtils;
